import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, LifeBuoy, FlaskConical, X } from 'lucide-react';

/**
 * ContactPopover — accessible contact disclosure.
 *
 * Exposes the three RendPleX mailboxes (Billing / Support / Research) so the
 * user never has to guess which one to use. Rendered as a small accessible
 * modal/dialog. Keyboard-closable with Esc. Backdrop click closes.
 *
 * Trigger element is provided via `trigger` prop. If omitted, a plain
 * `data-testid="contact-trigger"` link using `label` is rendered.
 */
export default function ContactPopover({ trigger, triggerClassName = '' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // Focus close button for accessibility
    setTimeout(() => closeBtnRef.current?.focus(), 10);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const rows = [
    {
      key: 'billing',
      icon: CreditCard,
      email: 'billing@rendplex.com',
      titleKey: 'contact.billing.title',
      descKey: 'contact.billing.desc',
      testid: 'contact-billing',
    },
    {
      key: 'support',
      icon: LifeBuoy,
      email: 'support@rendplex.com',
      titleKey: 'contact.support.title',
      descKey: 'contact.support.desc',
      testid: 'contact-support',
    },
    {
      key: 'research',
      icon: FlaskConical,
      email: 'research@rendplex.com',
      titleKey: 'contact.research.title',
      descKey: 'contact.research.desc',
      testid: 'contact-research',
    },
  ];

  const TriggerEl = trigger ? (
    React.cloneElement(trigger, {
      onClick: (e) => { trigger.props.onClick?.(e); setOpen(true); },
      'data-testid': trigger.props['data-testid'] || 'contact-trigger',
      'aria-haspopup': 'dialog',
      'aria-expanded': open,
    })
  ) : (
    <button
      type="button"
      data-testid="contact-trigger"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => setOpen(true)}
      className={triggerClassName || 'text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white transition-colors'}
    >
      {t('common.contact')}
    </button>
  );

  return (
    <>
      {TriggerEl}
      {open && (
        <div
          data-testid="contact-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div
            ref={dialogRef}
            data-testid="contact-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-dialog-title"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-card-hover overflow-hidden"
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-2">
              <div>
                <h2 id="contact-dialog-title" className="font-primary font-bold text-xl text-[#12355B] dark:text-white">
                  {t('contact.title')}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('contact.subtitle')}
                </p>
              </div>
              <button
                ref={closeBtnRef}
                type="button"
                data-testid="contact-close"
                aria-label={t('common.close')}
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-[#45C04A]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              {rows.map((row) => {
                const Icon = row.icon;
                return (
                  <a
                    key={row.key}
                    data-testid={row.testid}
                    href={`mailto:${row.email}`}
                    className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-[#45C04A] hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#45C04A]"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#12355B]/5 dark:bg-[#45C04A]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#12355B] dark:text-[#45C04A]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-900 dark:text-white">
                        {t(row.titleKey)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {t(row.descKey)}
                      </div>
                      <div className="text-sm font-mono text-[#45C04A] mt-1 break-all">
                        {row.email}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
            <div className="px-6 pb-5 pt-1 text-[11px] text-slate-400 dark:text-slate-500">
              {t('contact.hint')}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
